import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  PrinterIcon,
  CloudArrowDownIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';
import { WebPageSchema, BreadcrumbSchema } from '../../components/SEO/StructuredData';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function DentalConsentForms() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const templatesRef = useRef(null);
  const featuresRef = useRef(null);
  const testimonialsRef = useRef(null);
  
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set page meta
  usePageMeta(
    'AI Dental Consent Forms for UK Practices | Smile Consent',
    'AI dental consent forms for UK practices. Generate treatment-specific consent forms, add risks and costs, and send for digital signature with clear audit trails.',
    'https://smileconsent.co.uk/dental-consent-forms'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setFormData({ practiceName: '', email: '', phone: '', numberOfDentists: '1-2' });
      setShowRegistrationForm(false);
      setIsSubmitting(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleScheduleDemo = () => {
    navigate('/book-demo');
  };

  const consentTemplates = [
    {
      category: 'Implant Dentistry',
      forms: [
        { name: 'Single Tooth Implant Consent', complexity: 'High', time: '15 min', gdc: true },
        { name: 'Multiple Implant Consent', complexity: 'High', time: '20 min', gdc: true },
        { name: 'Bone Graft Consent', complexity: 'High', time: '18 min', gdc: true },
        { name: 'Sinus Lift Consent', complexity: 'High', time: '25 min', gdc: true }
      ]
    },
    {
      category: 'Orthodontics',
      forms: [
        { name: 'Fixed Braces Consent', complexity: 'Medium', time: '12 min', gdc: true },
        { name: 'Invisalign Consent', complexity: 'Medium', time: '10 min', gdc: true },
        { name: 'Retainer Consent', complexity: 'Low', time: '8 min', gdc: true },
        { name: 'Orthognathic Surgery Consent', complexity: 'High', time: '30 min', gdc: true }
      ]
    },
    {
      category: 'Oral Surgery',
      forms: [
        { name: 'Simple Extraction Consent', complexity: 'Low', time: '8 min', gdc: true },
        { name: 'Surgical Extraction Consent', complexity: 'Medium', time: '12 min', gdc: true },
        { name: 'Wisdom Tooth Removal Consent', complexity: 'Medium', time: '15 min', gdc: true },
        { name: 'Biopsy Consent', complexity: 'Medium', time: '10 min', gdc: true }
      ]
    },
    {
      category: 'Cosmetic Dentistry',
      forms: [
        { name: 'Veneer Consent', complexity: 'Medium', time: '12 min', gdc: true },
        { name: 'Crown Consent', complexity: 'Medium', time: '10 min', gdc: true },
        { name: 'Bleaching Consent', complexity: 'Low', time: '8 min', gdc: true },
        { name: 'Composite Bonding Consent', complexity: 'Low', time: '6 min', gdc: true }
      ]
    },
    {
      category: 'Periodontics',
      forms: [
        { name: 'Scaling & Root Planing Consent', complexity: 'Medium', time: '10 min', gdc: true },
        { name: 'Gum Surgery Consent', complexity: 'High', time: '20 min', gdc: true },
        { name: 'Crown Lengthening Consent', complexity: 'High', time: '18 min', gdc: true },
        { name: 'Gum Graft Consent', complexity: 'High', time: '25 min', gdc: true }
      ]
    },
    {
      category: 'Endodontics',
      forms: [
        { name: 'Root Canal Consent', complexity: 'Medium', time: '12 min', gdc: true },
        { name: 'Retreatment Consent', complexity: 'High', time: '15 min', gdc: true },
        { name: 'Apicectomy Consent', complexity: 'High', time: '20 min', gdc: true }
      ]
    }
  ];

  const gdcRequirements = [
    'Informed consent documentation',
    'Treatment risks and benefits',
    'Alternative treatment options',
    'Cost implications clearly stated',
    'Patient capacity assessment',
    'Consent withdrawal procedures',
    'Emergency contact information',
    'Follow-up care instructions'
  ];

  const practiceTypes = [
    {
      name: 'Implant Dentistry',
      description: 'Comprehensive consent forms for single and multiple implants, bone grafts, and sinus lifts.',
      forms: '12+ specialized forms'
    },
    {
      name: 'Orthodontics',
      description: 'Generate consent for fixed braces, Invisalign, and orthognathic surgery.',
      forms: '8+ specialized forms'
    },
    {
      name: 'Oral Surgery',
      description: 'Complete consent coverage for extractions, wisdom teeth removal, and biopsies.',
      forms: '6+ specialized forms'
    }
  ];

  return (
    <>
      <Helmet>
        <title>AI Dental Consent Forms for UK Practices | Smile Consent</title>
        <meta name="description" content="AI dental consent forms for UK practices. Generate treatment-specific consent forms, add risks and costs, and send for digital signature with clear audit trails." />
        <meta name="keywords" content="AI dental consent forms, dental consent forms UK, digital dental consent forms, GDC compliant dental consent forms, CQC consent forms" />
        <link rel="canonical" href="https://smileconsent.co.uk/dental-consent-forms" />
        
        {/* Open Graph */}
        <meta property="og:title" content="AI Dental Consent Forms for UK Practices | Smile Consent" />
        <meta property="og:description" content="AI dental consent forms for UK practices with digital signing, treatment-specific wording, and clear audit trails." />
        <meta property="og:url" content="https://smileconsent.co.uk/dental-consent-forms" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Dental Consent Forms for UK Practices | Smile Consent" />
        <meta name="twitter:description" content="AI dental consent forms for UK practices with digital signing, treatment-specific wording, and clear audit trails." />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(WebPageSchema({
            name: "AI Dental Consent Forms",
            url: "https://smileconsent.co.uk/dental-consent-forms",
            description: "AI dental consent forms with digital signatures for UK dental practices"
          }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(BreadcrumbSchema({
            items: [{ label: 'Dental Consent Forms', path: '/dental-consent-forms' }]
          }))}
        </script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Navigation />
        
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Breadcrumb items={[
            { label: 'Dental Consent Forms', path: '/dental-consent-forms' }
          ]} />
        </div>

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            AI Dental Consent Forms for UK Practices
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Generate treatment-specific dental consent forms in under a minute. Add patients, treatments, risks, and costs, then send for digital signature with a clear audit trail.
          </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <DocumentCheckIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button 
                onClick={handleScheduleDemo}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <CalendarIcon className="h-6 w-6" />
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* GDC Compliance Banner */}
      <section className="bg-green-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ShieldCheckIcon className="h-8 w-8" />
              <h2 className="text-2xl font-bold">100% GDC & CQC Compliant</h2>
            </div>
            <p className="text-lg text-green-100 max-w-3xl mx-auto">
              All consent forms are regularly updated to meet the latest GDC standards and CQC requirements. 
              Legal protection for your practice, peace of mind for your patients.
            </p>
          </div>
        </div>
      </section>

      {/* Consent Templates Section */}
      <section ref={templatesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Treatment-Specific Consent, AI-Generated
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Add diagnosis, treatment, tooth and cost—then generate consent text for any procedure. 
              UK-law ready, detailed risks and alternatives, multiple treatments per consent.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consentTemplates.map((category, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{category.category}</h3>
                </div>
                <div className="space-y-3">
                  {category.forms.map((form, formIndex) => (
                    <div key={formIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{form.name}</h4>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-4 w-4" />
                            {form.time}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            form.complexity === 'High' ? 'bg-red-100 text-red-800' :
                            form.complexity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {form.complexity}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {form.gdc && (
                          <div className="bg-green-100 p-1 rounded">
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          </div>
                        )}
                        <button className="text-gray-400 cursor-not-allowed" disabled>
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button className="w-full bg-gray-400 text-white py-2 px-4 rounded-lg cursor-not-allowed flex items-center justify-center gap-2" disabled>
                    <CloudArrowDownIcon className="h-4 w-4" />
                    Download All Forms
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GDC Requirements Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              GDC Compliance Requirements
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our consent forms meet all General Dental Council requirements for informed consent documentation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {gdcRequirements.map((requirement, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg animate-on-scroll">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  <h3 className="font-semibold text-gray-900">✓ Included</h3>
                </div>
                <p className="text-gray-600">{requirement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Consent Forms?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional, compliant, and designed to protect both your practice and your patients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center animate-on-scroll">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">GDC Compliant</h3>
              <p className="text-gray-600">All forms meet current General Dental Council standards and are regularly updated.</p>
            </div>

            <div className="text-center animate-on-scroll">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Time Saving</h3>
              <p className="text-gray-600">Reduce consent process from 45 minutes to 15 minutes with our streamlined forms.</p>
            </div>

            <div className="text-center animate-on-scroll">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <PencilIcon className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customizable</h3>
              <p className="text-gray-600">Easily modify forms to match your practice's specific requirements and branding.</p>
            </div>

            <div className="text-center animate-on-scroll">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <PrinterIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Print Ready</h3>
              <p className="text-gray-600">Professional formatting that looks great when printed or displayed digitally.</p>
            </div>

            <div className="text-center animate-on-scroll">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Risk Management</h3>
              <p className="text-gray-600">Comprehensive risk disclosure protects your practice from legal issues.</p>
            </div>

            <div className="text-center animate-on-scroll">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CpuChipIcon className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Digital Integration</h3>
              <p className="text-gray-600">Seamlessly integrate with your practice management software and digital workflows.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Types Section */}
      <section ref={testimonialsRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Forms for Every Practice Type
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Generate consent for any dental specialty and practice type—AI produces UK-law ready wording.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {practiceTypes.map((practice, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg animate-on-scroll">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{practice.name}</h3>
                </div>
                <p className="text-gray-600 mb-4">{practice.description}</p>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Available Forms:</span>
                    <span className="text-sm font-bold text-blue-600">{practice.forms}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Navigation Section for SEO */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InternalLinks
            title="Explore Treatment-Specific Dental Consent Forms"
            links={[
              {
                title: "Implant consent form UK",
                description: "Consent wording for implant treatment and grafting discussions",
                path: "/implant-consent-form-uk",
                icon: <DocumentTextIcon className="w-6 h-6" />
              },
              {
                title: "Orthodontic consent form UK",
                description: "Digital consent for braces, aligners, retention, and relapse",
                path: "/orthodontic-consent-form-uk",
                icon: <ClipboardDocumentListIcon className="w-6 h-6" />
              },
              {
                title: "Tooth extraction consent form UK",
                description: "Consent records for routine and surgical extraction",
                path: "/tooth-extraction-consent-form-uk",
                icon: <ExclamationTriangleIcon className="w-6 h-6" />
              },
              {
                title: "Root canal consent form UK",
                description: "Endodontic consent with alternatives and prognosis",
                path: "/root-canal-consent-form-uk",
                icon: <ShieldCheckIcon className="w-6 h-6" />
              },
              {
                title: "Composite bonding consent form UK",
                description: "Cosmetic consent wording for bonding limitations and maintenance",
                path: "/composite-bonding-consent-form-uk",
                icon: <SparklesIcon className="w-6 h-6" />
              },
              {
                title: "Veneer consent form UK",
                description: "Consent records for porcelain and composite veneers",
                path: "/veneer-consent-form-uk",
                icon: <DocumentCheckIcon className="w-6 h-6" />
              },
              {
                title: "Wisdom tooth consent form UK",
                description: "Surgical extraction consent with aftercare and risk disclosure",
                path: "/wisdom-tooth-consent-form-uk",
                icon: <ExclamationTriangleIcon className="w-6 h-6" />
              },
              {
                title: "Sedation consent form UK",
                description: "Consent records for sedation suitability and recovery guidance",
                path: "/sedation-consent-form-uk",
                icon: <ClockIcon className="w-6 h-6" />
              },
              {
                title: "Digital consent for dentists",
                description: "Paperless consent workflows for modern practices",
                path: "/digital-consent-dentists",
                icon: <DevicePhoneMobileIcon className="w-6 h-6" />
              },
              {
                title: "Dental consent software",
                description: "See the full consent platform for UK practices",
                path: "/dental-consent-software",
                icon: <CpuChipIcon className="w-6 h-6" />
              }
            ]}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Streamline Your Consent Process?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of dental practices using AI-generated consent. 
            Start your free trial today and see the difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <Link
              to="/dental-consent-plans"
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <ChartBarIcon className="h-6 w-6" />
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Name</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Dentists</label>
                <select
                  name="numberOfDentists"
                  value={formData.numberOfDentists}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1-2">1-2 Dentists</option>
                  <option value="3-5">3-5 Dentists</option>
                  <option value="6-10">6-10 Dentists</option>
                  <option value="10+">10+ Dentists</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">SmileConsent</h3>
              <p className="text-gray-400 text-sm">
                Professional dental consent management for UK practices. GDC & CQC compliant forms.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-white text-sm">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}